import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useClientAuth } from '../contexts/ClientAuthContext';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Calendar } from '../components/ui/calendar';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { CalendarDays, Clock, User, LogOut, XCircle, Building, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { format, addDays } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MyBookingsPage() {
  const { clientUser, logout, loading: authLoading } = useClientAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('upcoming');
  const [rescheduleAppt, setRescheduleAppt] = useState(null);
  const [rescheduleDate, setRescheduleDate] = useState(null);
  const [rescheduleSlots, setRescheduleSlots] = useState([]);
  const [rescheduleTime, setRescheduleTime] = useState('');
  const [rescheduleLoading, setRescheduleLoading] = useState(false);

  useEffect(() => {
    if (!authLoading && !clientUser) navigate('/client-login');
  }, [authLoading, clientUser, navigate]);

  const fetchAppointments = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/client-portal/appointments`, { withCredentials: true });
      setAppointments(data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (clientUser) fetchAppointments(); }, [clientUser, fetchAppointments]);

  const cancelAppointment = async (id) => {
    if (!window.confirm('Are you sure you want to cancel this appointment?')) return;
    try {
      await axios.put(`${API}/client-portal/appointments/${id}/cancel`, {}, { withCredentials: true });
      toast.success('Appointment cancelled');
      fetchAppointments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel');
    }
  };

  const openReschedule = (appt) => {
    setRescheduleAppt(appt);
    setRescheduleDate(addDays(new Date(), 1));
    setRescheduleSlots([]);
    setRescheduleTime('');
  };

  const fetchRescheduleSlots = useCallback(async (date) => {
    if (!rescheduleAppt) return;
    setRescheduleLoading(true);
    try {
      const slug = rescheduleAppt.business_slug;
      if (!slug) { setRescheduleSlots([]); setRescheduleLoading(false); return; }
      const dateStr = format(date, 'yyyy-MM-dd');
      const { data } = await axios.get(`${API}/public/book/${slug}/availability`, {
        params: { staff_id: rescheduleAppt.staff_id, date: dateStr, duration: rescheduleAppt.duration || 30 }
      });
      setRescheduleSlots(data.available_slots || []);
    } catch { setRescheduleSlots([]); }
    setRescheduleLoading(false);
  }, [rescheduleAppt]);

  useEffect(() => {
    if (rescheduleDate && rescheduleAppt) fetchRescheduleSlots(rescheduleDate);
  }, [rescheduleDate, rescheduleAppt, fetchRescheduleSlots]);

  const confirmReschedule = async () => {
    if (!rescheduleAppt || !rescheduleDate || !rescheduleTime) return;
    try {
      await axios.put(`${API}/client-portal/appointments/${rescheduleAppt.id}/reschedule`, {
        date: format(rescheduleDate, 'yyyy-MM-dd'), start_time: rescheduleTime
      }, { withCredentials: true });
      toast.success('Appointment rescheduled!');
      setRescheduleAppt(null);
      fetchAppointments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reschedule');
    }
  };

  const today = new Date().toISOString().split('T')[0];
  const upcoming = appointments.filter(a => a.date >= today && a.status === 'confirmed');
  const past = appointments.filter(a => a.date < today || a.status !== 'confirmed');
  const displayed = filter === 'upcoming' ? upcoming : past;

  const handleLogout = async () => {
    await logout();
    navigate('/get-started');
  };

  if (authLoading || !clientUser) return null;

  return (
    <div className="min-h-screen bg-[#F9F8F6]" data-testid="my-bookings-page">
      <header className="bg-white border-b border-[#E5E7E1] sticky top-0 z-20">
        <div className="max-w-3xl mx-auto px-6 h-16 flex items-center justify-between">
          <h1 className="text-xl font-bold text-[#556B2F]" style={{ fontFamily: 'Outfit' }}>Apptio</h1>
          <div className="flex items-center gap-3">
            <LanguageSwitcher variant="nav" />
            <span className="text-sm text-[#5C6656]">{clientUser.name}</span>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-[#E2725B]" data-testid="client-logout-btn">
              <LogOut size={16} />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-[#1A2421]" style={{ fontFamily: 'Outfit' }}>My Bookings</h2>
          <p className="text-sm text-[#5C6656] mt-1">View, reschedule, or cancel your appointments</p>
        </div>

        <div className="flex gap-2 mb-6">
          <Button data-testid="filter-upcoming" variant={filter === 'upcoming' ? 'default' : 'outline'}
            className={filter === 'upcoming' ? 'bg-[#556B2F] text-white' : 'border-[#E5E7E1] text-[#5C6656]'}
            onClick={() => setFilter('upcoming')}>
            Upcoming ({upcoming.length})
          </Button>
          <Button data-testid="filter-past" variant={filter === 'past' ? 'default' : 'outline'}
            className={filter === 'past' ? 'bg-[#556B2F] text-white' : 'border-[#E5E7E1] text-[#5C6656]'}
            onClick={() => setFilter('past')}>
            Past ({past.length})
          </Button>
        </div>

        {loading ? (
          <p className="text-center text-[#5C6656] py-12">{t('common.loading')}</p>
        ) : displayed.length === 0 ? (
          <Card className="border-[#E5E7E1]">
            <CardContent className="py-12 text-center">
              <CalendarDays size={40} className="mx-auto text-[#E5E7E1] mb-4" />
              <p className="text-[#5C6656]">{filter === 'upcoming' ? 'No upcoming appointments' : 'No past appointments'}</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {displayed.map(appt => (
              <Card key={appt.id} data-testid={`booking-${appt.id}`} className="border-[#E5E7E1] shadow-sm hover:shadow-md transition-shadow">
                <CardContent className="p-5">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge className={`text-xs ${appt.status === 'confirmed' ? 'bg-[#556B2F] text-white' : appt.status === 'completed' ? 'bg-[#3E6641] text-white' : appt.status === 'cancelled' ? 'bg-[#E2725B] text-white' : 'bg-[#D4A373] text-white'}`}>
                          {appt.status}
                        </Badge>
                        {appt.business_name && (
                          <span className="flex items-center gap-1 text-xs text-[#5C6656]">
                            <Building size={10} /> {appt.business_name}
                          </span>
                        )}
                      </div>
                      <h3 className="font-semibold text-[#1A2421]" style={{ fontFamily: 'Outfit' }}>{appt.service_name}</h3>
                      <div className="mt-2 space-y-1">
                        <p className="flex items-center gap-2 text-sm text-[#5C6656]">
                          <CalendarDays size={14} className="text-[#556B2F]" />
                          {new Date(appt.date + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                        </p>
                        <p className="flex items-center gap-2 text-sm text-[#5C6656]">
                          <Clock size={14} className="text-[#556B2F]" />
                          {appt.start_time} - {appt.end_time} ({appt.duration}min)
                        </p>
                        <p className="flex items-center gap-2 text-sm text-[#5C6656]">
                          <User size={14} className="text-[#556B2F]" />
                          {appt.staff_name}
                        </p>
                      </div>
                    </div>
                    <div className="text-right flex flex-col items-end gap-2">
                      <p className="text-xl font-bold text-[#556B2F]" style={{ fontFamily: 'Outfit' }}>{'\u00A3'}{appt.price?.toFixed(2)}</p>
                      {appt.status === 'confirmed' && appt.date >= today && (
                        <>
                          <Button variant="outline" size="sm" onClick={() => openReschedule(appt)}
                            className="border-[#556B2F] text-[#556B2F] hover:bg-[#556B2F] hover:text-white text-xs"
                            data-testid={`reschedule-booking-${appt.id}`}>
                            <RefreshCw size={12} className="mr-1" /> Reschedule
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => cancelAppointment(appt.id)}
                            className="border-[#E2725B] text-[#E2725B] hover:bg-[#E2725B] hover:text-white text-xs"
                            data-testid={`cancel-booking-${appt.id}`}>
                            <XCircle size={12} className="mr-1" /> Cancel
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Reschedule Dialog */}
      <Dialog open={!!rescheduleAppt} onOpenChange={() => setRescheduleAppt(null)}>
        <DialogContent className="sm:max-w-md border-[#E5E7E1]">
          <DialogHeader>
            <DialogTitle className="text-[#1A2421]" style={{ fontFamily: 'Outfit' }}>
              <RefreshCw size={18} className="inline mr-2 text-[#556B2F]" />
              Reschedule Appointment
            </DialogTitle>
          </DialogHeader>
          {rescheduleAppt && (
            <div className="space-y-4 mt-2">
              <div className="p-3 bg-[#F9F8F6] rounded-xl border border-[#E5E7E1]">
                <p className="font-medium text-[#1A2421] text-sm">{rescheduleAppt.service_name}</p>
                <p className="text-xs text-[#5C6656] mt-0.5">with {rescheduleAppt.staff_name} · {rescheduleAppt.duration}min</p>
                <p className="text-xs text-[#5C6656]">Currently: {rescheduleAppt.date} at {rescheduleAppt.start_time}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-[#1A2421] mb-2">Pick a new date</p>
                <Calendar
                  mode="single"
                  selected={rescheduleDate}
                  onSelect={(d) => { if (d) { setRescheduleDate(d); setRescheduleTime(''); } }}
                  disabled={(date) => date < new Date()}
                  className="rounded-xl border border-[#E5E7E1]"
                />
              </div>
              {rescheduleDate && (
                <div>
                  <p className="text-sm font-medium text-[#1A2421] mb-2">
                    Available times for {format(rescheduleDate, 'EEE d MMM')}
                  </p>
                  {rescheduleLoading ? (
                    <p className="text-sm text-[#5C6656]">Loading...</p>
                  ) : rescheduleSlots.length === 0 ? (
                    <p className="text-sm text-[#E2725B]">No available slots on this date</p>
                  ) : (
                    <div className="grid grid-cols-4 gap-2 max-h-48 overflow-y-auto">
                      {rescheduleSlots.map(slot => (
                        <button key={slot} type="button" onClick={() => setRescheduleTime(slot)}
                          className={`py-2 px-3 rounded-lg text-sm font-medium border transition-colors ${rescheduleTime === slot ? 'bg-[#556B2F] text-white border-[#556B2F]' : 'border-[#E5E7E1] text-[#1A2421] hover:border-[#556B2F]'}`}
                          data-testid={`reschedule-slot-${slot}`}>
                          {slot}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <Button onClick={confirmReschedule} disabled={!rescheduleTime}
                className="w-full bg-[#556B2F] hover:bg-[#4A5D23] text-white disabled:opacity-40" data-testid="confirm-reschedule-btn">
                Confirm Reschedule
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
